import React from 'react';
import { ConnectorStatus, SimulatedData } from '../types';
import { BoltIcon, StopIcon, PlayIcon, PowerIcon, LockClosedIcon } from '@heroicons/react/24/outline';

interface ConnectorPanelProps {
  connector: {
    id: number;
    status: ConnectorStatus;
    currentPower: number;
    maxPower: number;
    current: number;
    chargePercentage: number;
    currentMeterReading?: string;
    activeSession?: {
      transactionId: string;
      idTag: string;
      startTime: Date;
      energyConsumed: number;
      sessionEnergyKwh?: string;
      chargingInterval?: NodeJS.Timeout;
    };
  };
  simulatedData: SimulatedData;
  isConnected: boolean;
  disabled?: boolean;
  onStatusChange: (connectorId: number, status: ConnectorStatus) => void;
  onStartCharging: (connectorId: number) => void;
  onStopCharging: (connectorId: number) => void;
  onSendMeterValues: (connectorId: number) => void;
  onPlugInCar: (connectorId: number) => void;
  onUnplugCar: (connectorId: number) => void;
}

const ConnectorPanel: React.FC<ConnectorPanelProps> = ({
  connector,
  simulatedData,
  isConnected,
  disabled,
  onStatusChange,
  onStartCharging,
  onStopCharging,
  onSendMeterValues,
  onPlugInCar,
  onUnplugCar
}) => {
  // ⚡ NEW: Check if the station is explicitly locked by the admin or rejected
  const isHardwareLocked = connector.status === ConnectorStatus.UNAVAILABLE || disabled;
  const isInteractionBlocked = !isConnected || isHardwareLocked;

  const getStatusColor = (status: ConnectorStatus) => {
    switch (status) {
      case ConnectorStatus.AVAILABLE: return 'bg-green-500';
      case ConnectorStatus.PREPARING: return 'bg-blue-500';
      case ConnectorStatus.OCCUPIED: return 'bg-yellow-500';
      case ConnectorStatus.RESERVED: return 'bg-purple-500';
      case ConnectorStatus.UNAVAILABLE: return 'bg-red-600'; // Made red to indicate lockdown
      case ConnectorStatus.FAULTED: return 'bg-red-600';
      default: return 'bg-gray-400';
    }
  };

  const renderMeterValues = () => (
    <div className="mt-4 space-y-2">
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gray-50 p-2 rounded">
          <div className="text-sm text-gray-600">Power</div>
          <div className="font-medium">{connector.currentPower} W</div>
        </div>
        <div className="bg-gray-50 p-2 rounded">
          <div className="text-sm text-gray-600">Current</div>
          <div className="font-medium">{connector.current} A</div>
        </div>
      </div>
      {connector.activeSession && (
        <div className="bg-blue-50 p-2 rounded">
          <div className="text-sm text-blue-600">Energy Delivered</div>
          <div className="font-medium text-blue-900">
            {connector.activeSession.sessionEnergyKwh || '0.00'} kWh
          </div>
        </div>
      )}
    </div>
  );

  const renderChargingProgress = () => {
    if (!connector.activeSession) return null;

    return (
      <div className="mt-4">
        <div className="flex justify-between text-sm text-gray-600 mb-1">
          <span>Charge Progress</span>
          <span>{connector.chargePercentage}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-green-600 h-2 rounded-full transition-all duration-500"
            style={{ width: `${connector.chargePercentage}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={`bg-white rounded-lg shadow-md border ${isHardwareLocked ? 'border-red-300 relative overflow-hidden' : 'border-gray-200'} p-6`}>
      
      {/* ⚡ NEW: Admin Lockdown Banner */}
      {isHardwareLocked && (
        <div className="absolute top-0 left-0 w-full bg-red-100 border-b border-red-200 px-4 py-2 flex items-center justify-center space-x-2 z-10">
          <LockClosedIcon className="w-4 h-4 text-red-600" />
          <span className="text-xs font-bold text-red-700 uppercase tracking-wider">
            Station Offline (Locked by Admin)
          </span>
        </div>
      )}

      <div className={`flex items-center justify-between mb-4 ${isHardwareLocked ? 'mt-8' : ''}`}>
        <h3 className="text-lg font-semibold text-gray-900">
          Connector {connector.id}
        </h3>
        <div className={`w-3 h-3 rounded-full ${getStatusColor(connector.status)}`} />
      </div>

      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Status:</span>
          {/* ⚡ NEW: Explicit Status Text */}
          <span className={`font-medium ${connector.status === ConnectorStatus.UNAVAILABLE ? 'text-red-600 font-bold text-sm' : 'capitalize'}`}>
            {connector.status === ConnectorStatus.UNAVAILABLE ? 'UNAVAILABLE (Admin Locked)' : connector.status.toLowerCase()}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-gray-600">Max Power:</span>
          <span className="font-medium">{connector.maxPower} W</span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4 border-t pt-4">
          <div>
            <span className="text-xs text-gray-500 uppercase font-semibold">Meter Start</span>
            <div className="text-lg font-mono text-blue-700">
              {connector.activeSession ? `${connector.activeSession.energyConsumed.toFixed(0)} Wh` : '---'}
            </div>
          </div>
          <div>
            <span className="text-xs text-gray-500 uppercase font-semibold">Current Meter</span>
            <div className="text-lg font-mono text-green-700">
              {connector.currentMeterReading ? `${connector.currentMeterReading} Wh` : '---'}
            </div>
          </div>
        </div>

        {connector.activeSession && (
          <div className="bg-blue-50 p-3 rounded-md">
            <div className="text-sm text-blue-800 font-medium mb-2">
              Active Session
            </div>
            <div className="space-y-1 text-sm text-blue-700">
              <div>ID Tag: {connector.activeSession.idTag}</div>
              <div>Transaction: {connector.activeSession.transactionId}</div>
              <div>Started: {connector.activeSession.startTime.toLocaleTimeString()}</div>
            </div>
          </div>
        )}
      </div>

      {renderMeterValues()}
      {renderChargingProgress()}

      <div className="mt-6 space-y-2">
        <div className="flex space-x-2">
          {/* Status Change Dropdown */}
          <select
            value={connector.status}
            onChange={(e) => onStatusChange(connector.id, e.target.value as ConnectorStatus)}
            disabled={isInteractionBlocked}
            className="flex-1 px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:text-gray-400"
          >
            {Object.values(ConnectorStatus).map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>

        <div className="flex space-x-2">
          {/* Plug In Car Button */}
          {connector.status === ConnectorStatus.AVAILABLE && !isHardwareLocked && (
            <button
              onClick={() => onPlugInCar(connector.id)}
              disabled={isInteractionBlocked}
              className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-300"
            >
              <PlayIcon className="h-4 w-4 mr-1" />
              Plug In Car
            </button>
          )}

          {/* Unplug Car Button */}
          {(connector.activeSession || connector.status === ConnectorStatus.OCCUPIED) && (
            <div className="flex-1">
              <div className="flex-1">
                <button
                  onClick={() => onUnplugCar(connector.id)}
                  disabled={isInteractionBlocked || connector.currentPower > 0}
                  className={`w-full inline-flex items-center justify-center px-3 py-2 border shadow-sm rounded-md ${connector.currentPower > 0 || isHardwareLocked ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                    }`}
                >
                  <PowerIcon className="h-4 w-4 mr-1" />
                  Unplug Car
                </button>
              </div>

              {connector.currentPower > 0 && (
                <p className="text-[10px] text-gray-500 mt-1 text-center">
                  Stop charging to unlock cable
                </p>
              )}

              {connector.activeSession && connector.currentPower === 0 && !isHardwareLocked && (
                <p className="text-[10px] text-blue-600 mt-1 text-center font-bold animate-pulse">
                  ✅ Charging Stopped. You can now unplug.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex space-x-2">
          {/* Start Charging Button */}
          {(connector.status === ConnectorStatus.PREPARING || connector.status === ConnectorStatus.OCCUPIED) &&
            !connector.activeSession && (
              <button
                onClick={() => onStartCharging(connector.id)}
                disabled={isInteractionBlocked}
                className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:text-gray-500 text-white font-medium py-2 px-4 rounded-md transition duration-150 ease-in-out flex items-center justify-center"
              >
                <PlayIcon className="w-4 h-4 mr-2" />
                Start Charging
              </button>
            )}

          {/* Stop Charging Button */}
          {connector.activeSession && connector.currentPower > 0 && (
            <button
              onClick={() => onStopCharging(connector.id)}
              disabled={!isConnected} // We allow stopping even if locked, so the user isn't trapped
              className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white font-medium py-2 px-4 rounded-md transition duration-150 ease-in-out flex items-center justify-center"
            >
              <StopIcon className="w-4 h-4 mr-2" />
              Stop Charging
            </button>
          )}
        </div>

        {/* Send Meter Values Button */}
        <button
          onClick={() => onSendMeterValues(connector.id)}
          disabled={isInteractionBlocked}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-500 text-white font-medium py-2 px-4 rounded-md transition duration-150 ease-in-out flex items-center justify-center"
        >
          <BoltIcon className="w-4 h-4 mr-2" />
          Send Meter Values
        </button>
      </div>
    </div>
  );
};

export default ConnectorPanel;